import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Search, Receipt, Filter, BriefcaseBusiness, Wallet, Building2 } from 'lucide-react'
import { toast } from 'sonner'

import { financeApi } from '@/services/finance'
import { workersApi } from '@/services/workers'
import { SearchableSelect } from '@/components/common/searchable-select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { JobTitleLink, formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { useOrganizationDateLocale } from '@/lib/org-date-locale'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { formatServerDateForLocale, getTenantNowInputValue, tenantInputToIso, useRequiredTenantTimeZone } from '@/lib/tenant-date-time'
import { getUserDropdownKeywords, getUserDropdownLabel } from '@/lib/user-display'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { getErrorMessage } from '@/lib/api'
import { formatCurrency } from '@/lib/utils'

type CompanyPaymentSummaryRow = {
  id: string | number
  worker_id: string | number
  worker_name: string
  worker_avatar?: string
  jobs_count: number
  total_due: number
  total_paid: number
  pending_amount: number
  last_activity?: string | null
  status: 'paid' | 'partial' | 'pending' | 'overpaid'
}

type CompanyPaymentsSummaryResponse = {
  count: number
  results: CompanyPaymentSummaryRow[]
  summary?: {
    total_collected: number
    total_returned: number
    total_pending: number
    workers_count: number
    workers_settled: number
    jobs_with_dues: number
    average_payment: number
  }
}

type CompanyPaymentJobRow = {
  id: string | number
  assignment_id: string | number
  job_id?: string | number | null
  job_ref?: string | null
  job_title?: string | null
  completed_on?: string | null
  amount_due: number
  amount_paid: number
  pending_amount: number
  status: 'paid' | 'partial' | 'pending' | 'overpaid'
}

type CompanyPaymentJobsResponse = {
  worker?: {
    id: string | number
    name?: string
    balance_to_pay_to_company?: number
  }
  summary?: {
    total_due: number
    total_paid: number
    total_pending: number
    jobs_count: number
  }
  results: CompanyPaymentJobRow[]
  recent_payments?: Array<{
    id: string | number
    job_id?: string | number | null
    job_ref?: string | null
    job_title?: string | null
    amount: number
    payment_mode: string
    payment_date?: string | null
    reference?: string | null
  }>
}

type CompanyPaymentFormState = {
  amount: string
  payment_mode: 'cash' | 'bank' | 'upi' | 'cheque' | 'other'
  payment_date: string
  reference: string
  notes: string
}

const PAYMENT_MODE_OPTIONS: Array<{ value: CompanyPaymentFormState['payment_mode']; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
]

const DEFAULT_PAYMENT_FORM = (timeZone?: string | null): CompanyPaymentFormState => ({
  amount: '',
  payment_mode: 'cash',
  payment_date: getTenantNowInputValue(timeZone),
  reference: '',
  notes: '',
})

const getInitials = (name?: string) => {
  if (!name) return '??'
  return name.trim().split(/\s+/).filter(Boolean).map((part) => part[0]).join('').toUpperCase().slice(0, 2)
}

export default function CompanyPaymentsPage() {
  const queryClient = useQueryClient()
  const PAYMENT_STATUS_OPTIONS = {
    all: 'All statuses',
    pending: 'Pending',
    partial: 'Partial',
    paid: 'Paid',
    overpaid: 'Overpaid',
  } as const

  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const organizationDateLocale = useOrganizationDateLocale(serviceInstanceId)
  // One tenant timezone for the default and submit of payment_date; never the browser zone.
  const { timeZone: tenantTimeZone, isReady: isTenantTimeZoneReady } = useRequiredTenantTimeZone(serviceInstanceId)
  const initialSearch = searchParams.get('search') || ''
  const initialWorkerId = searchParams.get('worker_id') || 'all'
  const initialPaymentStatus = searchParams.get('payment_status') || 'all'
  const initialDateFrom = searchParams.get('date_from') || ''
  const initialDateTo = searchParams.get('date_to') || ''
  const initialPage = Number(searchParams.get('page') || '1')
  const initialPageSize = Number(searchParams.get('page_size') || '10')

  const [page, setPage] = useState(Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1)
  const [pageSize, setPageSize] = useState(Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 10)
  const [search, setSearch] = useState(initialSearch)
  const [selectedWorkerId, setSelectedWorkerId] = useState(initialWorkerId)
  const [paymentStatus, setPaymentStatus] = useState(initialPaymentStatus)
  const [dateFrom, setDateFrom] = useState(initialDateFrom)
  const [dateTo, setDateTo] = useState(initialDateTo)
  const [detailWorker, setDetailWorker] = useState<CompanyPaymentSummaryRow | null>(null)
  const [selectedJob, setSelectedJob] = useState<CompanyPaymentJobRow | null>(null)
  const [recordDialogOpen, setRecordDialogOpen] = useState(false)
  const [paymentForm, setPaymentForm] = useState<CompanyPaymentFormState>(() => DEFAULT_PAYMENT_FORM(tenantTimeZone))

  // A form opened before the tenant timezone was known has an empty payment_date; fill it once it is.
  useEffect(() => {
    if (tenantTimeZone) {
      setPaymentForm((current) => (current.payment_date ? current : { ...current, payment_date: getTenantNowInputValue(tenantTimeZone) }))
    }
  }, [tenantTimeZone])
  const deferredSearch = useDeferredValue(search.trim())

  useEffect(() => {
    setSearch(searchParams.get('search') || '')
    setSelectedWorkerId(searchParams.get('worker_id') || 'all')
    setPaymentStatus(searchParams.get('payment_status') || 'all')
    setDateFrom(searchParams.get('date_from') || '')
    setDateTo(searchParams.get('date_to') || '')
    setPage(Number(searchParams.get('page') || '1') || 1)
    setPageSize(Number(searchParams.get('page_size') || '10') || 10)
  }, [searchParams])

  useEffect(() => {
    const params = new URLSearchParams()
    if (serviceInstanceId) params.set('service_instance_id', serviceInstanceId)
    if (deferredSearch) params.set('search', deferredSearch)
    if (selectedWorkerId !== 'all') params.set('worker_id', selectedWorkerId)
    if (paymentStatus !== 'all') params.set('payment_status', paymentStatus)
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (page > 1) params.set('page', String(page))
    if (pageSize !== 10) params.set('page_size', String(pageSize))

    const nextSearch = params.toString()
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search
    if (nextSearch !== currentSearch) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true })
    }
  }, [dateFrom, dateTo, deferredSearch, location.pathname, location.search, navigate, page, pageSize, paymentStatus, selectedWorkerId, serviceInstanceId])

  const { data: workersData } = useQuery({
    queryKey: ['company-payments-workers'],
    queryFn: () => workersApi.getWorkers({ page_size: 200, active: true }),
    staleTime: 60_000,
  })

  const workerFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All workers', keywords: 'all workers' },
      ...(workersData?.results ?? []).map((worker) => ({
        value: String(worker.id),
        label: getUserDropdownLabel(worker, worker.id),
        keywords: getUserDropdownKeywords(worker),
      })),
    ],
    [workersData?.results],
  )

  const { data, isLoading, error } = useQuery({
    queryKey: ['company-payments-summary', page, pageSize, selectedWorkerId, paymentStatus, dateFrom, dateTo, deferredSearch],
    queryFn: () => financeApi.getCompanyPaymentsSummary({
      page,
      page_size: pageSize,
      worker_id: selectedWorkerId !== 'all' ? selectedWorkerId : undefined,
      payment_status: paymentStatus !== 'all' ? paymentStatus : undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      search: deferredSearch || undefined,
    }),
    enabled: Boolean(serviceInstanceId),
  })

  const detailQuery = useQuery<CompanyPaymentJobsResponse>({
    queryKey: ['company-payment-jobs', detailWorker?.worker_id, dateFrom, dateTo],
    queryFn: () => financeApi.getCompanyPaymentJobs({
      worker_id: detailWorker ? String(detailWorker.worker_id) : undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
    enabled: Boolean(detailWorker?.worker_id && serviceInstanceId),
  })

  const summaryResponse = data as CompanyPaymentsSummaryResponse | undefined
  const rows = summaryResponse?.results ?? []
  const summaryData = summaryResponse?.summary ?? {
    total_collected: 0,
    total_returned: 0,
    total_pending: 0,
    workers_count: 0,
    workers_settled: 0,
    jobs_with_dues: 0,
    average_payment: 0,
  }

  const formatDateLabel = (value?: string | null) => formatServerDateForLocale(value, organizationDateLocale, tenantTimeZone)

  const openRecordDialog = (job?: CompanyPaymentJobRow | null) => {
    const pending = job?.pending_amount ?? detailQuery.data?.worker?.balance_to_pay_to_company ?? detailWorker?.pending_amount ?? 0
    setSelectedJob(job ?? null)
    setPaymentForm({
      ...DEFAULT_PAYMENT_FORM(tenantTimeZone),
      amount: pending > 0 ? String(pending) : '',
    })
    setRecordDialogOpen(true)
  }

  const closeRecordDialog = () => {
    setRecordDialogOpen(false)
    setSelectedJob(null)
    setPaymentForm(DEFAULT_PAYMENT_FORM(tenantTimeZone))
  }

  const recordPaymentMutation = useMutation({
    mutationFn: async () => {
      if (!detailWorker) throw new Error('Select a worker first.')
      const amount = Number(paymentForm.amount)
      if (!Number.isFinite(amount) || amount <= 0) throw new Error('Enter a valid payment amount greater than 0.')

      return financeApi.recordCompanyPayment({
        worker_id: detailWorker.worker_id,
        job_id: selectedJob?.job_id ?? undefined,
        amount,
        payment_mode: paymentForm.payment_mode,
        payment_date: tenantInputToIso(paymentForm.payment_date, tenantTimeZone),
        reference: paymentForm.reference || undefined,
        notes: paymentForm.notes || undefined,
      })
    },
    onSuccess: () => {
      toast.success('Payment to company recorded successfully')
      queryClient.invalidateQueries({ queryKey: ['company-payments-summary'] })
      queryClient.invalidateQueries({ queryKey: ['company-payment-jobs'] })
      if (detailWorker?.worker_id) {
        queryClient.invalidateQueries({ queryKey: ['worker', String(detailWorker.worker_id)] })
      }
      closeRecordDialog()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const renderStatusBadge = (status: CompanyPaymentSummaryRow['status'] | CompanyPaymentJobRow['status']) => {
    if (status === 'paid') return <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Paid</Badge>
    if (status === 'partial') return <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Partial</Badge>
    if (status === 'pending') return <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Pending</Badge>
    return <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">Overpaid</Badge>
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center rounded-[28px] border border-destructive/20 bg-card">
        <div className="text-center">
          <p className="mb-2 text-destructive">Failed to load payments to company</p>
          <Button variant="outline" onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-[calc(100vh-10rem)] flex-col gap-4 pb-2">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">Payments to company</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Cash collected by workers on jobs that must be returned to the company. This is separate from worker payouts.
          </p>
        </div>
        <div className="hidden flex-wrap items-center gap-2 rounded-xl border border-border/70 bg-muted/20 px-3 py-1.5 text-xs text-muted-foreground md:flex">
          <span className="inline-flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" />{formatCurrency(summaryData.total_collected)} collected</span>
          <span className="text-border">|</span>
          <span className="inline-flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" />{formatCurrency(summaryData.total_pending)} pending</span>
          <span className="text-border">|</span>
          <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{formatCurrency(summaryData.total_returned)} returned</span>
        </div>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border-border/70 bg-card shadow-sm">
        <div className="border-b border-border/60 bg-muted/10 p-3 sm:p-4">
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-6">
            <div className="relative xl:col-span-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search workers or jobs" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} className="h-9 rounded-lg border-border/70 bg-background pl-9" />
            </div>
            <SearchableSelect
              ariaLabel="Filter by worker"
              value={selectedWorkerId}
              onValueChange={(value) => { setSelectedWorkerId(value); setPage(1) }}
              options={workerFilterOptions}
              placeholder="All workers"
              searchPlaceholder="Search workers"
              emptyText="No workers found."
              triggerClassName="h-9 rounded-lg border-border/70 bg-background"
            />
            <Select value={paymentStatus} onValueChange={(value) => { setPaymentStatus(value); setPage(1) }}>
              <SelectTrigger className="h-9 rounded-lg border-border/70 bg-background"><Filter className="mr-2 h-3.5 w-3.5" /><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                {Object.entries(PAYMENT_STATUS_OPTIONS).map(([value, label]) => (<SelectItem key={value} value={value}>{label}</SelectItem>))}
              </SelectContent>
            </Select>
            <Input type="date" aria-label="Filter from date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1) }} className="h-9 rounded-lg border-border/70 bg-background" />
            <Input type="date" aria-label="Filter to date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1) }} className="h-9 rounded-lg border-border/70 bg-background" />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {isLoading ? (
            <div className="space-y-2 p-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-11 w-full rounded-lg" />)}</div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/80">
                <TableRow className="hover:bg-muted/95">
                  <TableHead className="h-10 pl-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Worker</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Jobs</TableHead>
                  <TableHead className="text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Collected</TableHead>
                  <TableHead className="text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Returned</TableHead>
                  <TableHead className="text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Pending</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Last activity</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Status</TableHead>
                  <TableHead className="pr-4 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length > 0 ? rows.map((row) => (
                  <TableRow key={row.id} className="border-border/60 hover:bg-muted/20">
                    <TableCell className="pl-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar className="h-8 w-8 border border-border/70">
                          <AvatarImage src={row.worker_avatar} />
                          <AvatarFallback className="bg-primary/10 text-xs text-primary">{getInitials(row.worker_name)}</AvatarFallback>
                        </Avatar>
                        <Link to={withServiceInstanceId(`/workers/${row.worker_id}`, serviceInstanceId)} className="truncate font-medium text-foreground hover:text-primary">{row.worker_name}</Link>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5 text-sm">{row.jobs_count}</TableCell>
                    <TableCell className="py-2.5 text-right font-medium tabular-nums">{formatCurrency(row.total_due)}</TableCell>
                    <TableCell className="py-2.5 text-right tabular-nums text-muted-foreground">{formatCurrency(row.total_paid)}</TableCell>
                    <TableCell className={`py-2.5 text-right font-semibold tabular-nums ${row.pending_amount > 0 ? 'text-amber-700' : 'text-foreground'}`}>{formatCurrency(row.pending_amount)}</TableCell>
                    <TableCell className="whitespace-nowrap py-2.5 text-sm text-muted-foreground">{formatDateLabel(row.last_activity)}</TableCell>
                    <TableCell className="py-2.5">{renderStatusBadge(row.status)}</TableCell>
                    <TableCell className="pr-4 py-2.5 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="outline" size="sm" className="h-8 rounded-lg px-2.5" onClick={() => setDetailWorker(row)}>
                          <BriefcaseBusiness className="mr-1.5 h-3.5 w-3.5" />Jobs
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 rounded-lg px-2.5" asChild>
                          <Link to={withServiceInstanceId(`/workers/${row.worker_id}`, serviceInstanceId)}>Worker</Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={8} className="h-40">
                      <div className="flex flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                        <Receipt className="h-9 w-9 opacity-50" />
                        <p className="font-medium text-foreground">No payments to company found</p>
                        <p className="text-sm">Workers with cash collected on jobs will appear here.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {!isLoading && summaryResponse?.count ? (
          <div className="border-t border-border/60 bg-muted/10 px-3 py-2 sm:px-4">
            <DataTablePagination page={page} pageSize={pageSize} totalCount={summaryResponse.count} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1) }} />
          </div>
        ) : null}
      </Card>

      <Dialog open={Boolean(detailWorker)} onOpenChange={(open) => !open && setDetailWorker(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{detailQuery.data?.worker?.name || detailWorker?.worker_name || 'Worker jobs'}</DialogTitle>
            <DialogDescription>Cash collected on jobs and payments returned to the company.</DialogDescription>
          </DialogHeader>
          {detailQuery.isLoading ? (
            <div className="space-y-3 py-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-4">
                <Card className="rounded-2xl border-border/70"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Jobs</p><p className="mt-1 text-2xl font-semibold">{detailQuery.data?.summary?.jobs_count ?? 0}</p></CardContent></Card>
                <Card className="rounded-2xl border-border/70"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Collected</p><p className="mt-1 text-2xl font-semibold">{formatCurrency(detailQuery.data?.summary?.total_due ?? 0)}</p></CardContent></Card>
                <Card className="rounded-2xl border-border/70"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Returned</p><p className="mt-1 text-2xl font-semibold">{formatCurrency(detailQuery.data?.summary?.total_paid ?? 0)}</p></CardContent></Card>
                <Card className="rounded-2xl border-border/70"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Pending</p><p className="mt-1 text-2xl font-semibold">{formatCurrency(detailQuery.data?.summary?.total_pending ?? 0)}</p></CardContent></Card>
              </div>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => openRecordDialog()} disabled={(detailQuery.data?.summary?.total_pending ?? 0) <= 0}>
                  <Wallet className="mr-2 h-4 w-4" />Record Payment
                </Button>
              </div>

              <div className="overflow-hidden rounded-2xl border border-border/70 bg-background">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Job</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Completed</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Collected</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Returned</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Pending</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Status</TableHead>
                      <TableHead className="text-right text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detailQuery.data?.results ?? []).length > 0 ? (detailQuery.data?.results ?? []).map((job) => (
                      <TableRow key={job.id}>
                        <TableCell>
                          {job.job_id ? (
                            <JobTitleLink
                              jobId={job.job_id}
                              title={job.job_title}
                              jobRef={job.job_ref}
                              serviceInstanceId={serviceInstanceId}
                              className="font-medium"
                            />
                          ) : '—'}
                          {job.job_ref ? <p className="text-xs text-muted-foreground">{job.job_ref}</p> : null}
                        </TableCell>
                        <TableCell>{formatDateLabel(job.completed_on)}</TableCell>
                        <TableCell>{formatCurrency(job.amount_due)}</TableCell>
                        <TableCell>{formatCurrency(job.amount_paid)}</TableCell>
                        <TableCell>{formatCurrency(job.pending_amount)}</TableCell>
                        <TableCell>{renderStatusBadge(job.status)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" onClick={() => openRecordDialog(job)} disabled={job.pending_amount <= 0}>
                            <Wallet className="mr-2 h-4 w-4" />Record
                          </Button>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow><TableCell colSpan={7} className="h-28 text-center text-muted-foreground">No jobs with cash due for this worker.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {(detailQuery.data?.recent_payments ?? []).length > 0 ? (
                <div className="rounded-2xl border border-border/70 bg-muted/10 p-4">
                  <p className="mb-3 text-sm font-medium text-foreground">Recent payments</p>
                  <div className="space-y-2">
                    {(detailQuery.data?.recent_payments ?? []).map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between text-sm">
                        <div>
                          <p className="font-medium">{formatCurrency(payment.amount)} · {payment.payment_mode}</p>
                          <p className="text-xs text-muted-foreground">
                            {payment.job_id
                              ? formatJobTitleWithId({
                                  jobId: payment.job_id,
                                  title: payment.job_title,
                                  jobRef: payment.job_ref,
                                })
                              : 'General payment'}
                            {payment.reference ? ` · ${payment.reference}` : ''}
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground">{formatDateLabel(payment.payment_date)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={recordDialogOpen} onOpenChange={(open) => !open && !recordPaymentMutation.isPending && closeRecordDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment to Company</DialogTitle>
            <DialogDescription>
              Record cash returned by {detailQuery.data?.worker?.name || detailWorker?.worker_name || 'this worker'}
              {selectedJob?.job_id ? ` for ${formatJobTitleWithId({
                jobId: selectedJob.job_id,
                title: selectedJob.job_title,
                jobRef: selectedJob.job_ref,
              })}` : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="company-payment-amount">Amount</Label>
                <Input id="company-payment-amount" type="number" min="0" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm((c) => ({ ...c, amount: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-payment-mode">Method</Label>
                <Select value={paymentForm.payment_mode} onValueChange={(value: CompanyPaymentFormState['payment_mode']) => setPaymentForm((c) => ({ ...c, payment_mode: value }))}>
                  <SelectTrigger id="company-payment-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_MODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-payment-date">Payment Date</Label>
                <Input id="company-payment-date" type="datetime-local" value={paymentForm.payment_date} onChange={(e) => setPaymentForm((c) => ({ ...c, payment_date: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-payment-ref">Reference</Label>
                <Input id="company-payment-ref" value={paymentForm.reference} onChange={(e) => setPaymentForm((c) => ({ ...c, reference: e.target.value }))} placeholder="Receipt or deposit reference" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="company-payment-notes">Notes</Label>
                <Textarea id="company-payment-notes" value={paymentForm.notes} onChange={(e) => setPaymentForm((c) => ({ ...c, notes: e.target.value }))} rows={3} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRecordDialog} disabled={recordPaymentMutation.isPending}>Cancel</Button>
            <Button
              onClick={() => {
                if (isTenantTimeZoneReady) {
                  recordPaymentMutation.mutate()
                }
              }}
              disabled={!isTenantTimeZoneReady || recordPaymentMutation.isPending}
            >
              {recordPaymentMutation.isPending ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
